import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface Props {
  canWrite: boolean;
  /** Всего позиций ревизии (без фильтра): без позиций ведомость не выгружается. */
  total: number;
  reviewCount: number;
  extracting: boolean;
  onExtract: () => void;
  exportingVed: boolean;
  onDownloadVedomost: () => Promise<void>;
  onOpenSupply: () => void;
  creatingAutoSq: boolean;
  onCreateSqFromRd: () => Promise<void>;
  onlyReview: boolean;
  onOnlyReviewChange: (checked: boolean) => void;
  /** Строк на проверке на открытой вкладке (без спрятанных повторов); null —
   *  открыта вкладка не части РД («Анализ комплектности»): фильтров позиций нет. */
  tabReviewCount: number | null;
  /** «Скрыть повторы вне суммы»; null — признаков отбора нет или открыта вкладка
   *  не части РД: переключателя нет. */
  hideRepeats: boolean | null;
  onHideRepeatsChange: (checked: boolean) => void;
  /** Сколько повторов вне суммы на открытой вкладке. */
  repeatCount: number;
  /** Действия правок РД «Инженер+» («Перенумеровать…»). */
  extra?: ReactNode;
}

/** Панель действий над ревизией, фильтр «Только на проверку» и «Скрыть повторы
 *  вне суммы» (повторы — по признаку отбора ведомости); фильтры — только на
 *  вкладках частей РД. */
export function VolumesToolbar({
  canWrite,
  total,
  reviewCount,
  extracting,
  onExtract,
  exportingVed,
  onDownloadVedomost,
  onOpenSupply,
  creatingAutoSq,
  onCreateSqFromRd,
  onlyReview,
  onOnlyReviewChange,
  tabReviewCount,
  hideRepeats,
  onHideRepeatsChange,
  repeatCount,
  extra,
}: Props) {
  return (
    <Controls>
      {canWrite && (
        <Button $variant="primary" $size="sm" disabled={extracting} onClick={onExtract}>
          {extracting ? 'Запуск…' : 'Извлечь заново'}
        </Button>
      )}
      <Button
        $variant="secondary"
        $size="sm"
        disabled={exportingVed || total === 0}
        onClick={onDownloadVedomost}
      >
        {exportingVed ? 'Формирование…' : 'Ведомость материалов (XLSX)'}
      </Button>
      {canWrite && (
        <Button
          $variant="secondary"
          $size="sm"
          onClick={onOpenSupply}
          title="Заявка в снабжение по этому документу РД: снабжение само сформирует ведомость материалов"
        >
          Передать в снабжение
        </Button>
      )}
      {canWrite && reviewCount > 0 && (
        <Button
          $variant="secondary"
          $size="sm"
          disabled={creatingAutoSq}
          onClick={() => void onCreateSqFromRd()}
          title="Автоматически собрать замечания входного контроля РД в запрос SQ проектному институту"
        >
          {creatingAutoSq ? 'Сбор SQ…' : `⚡ В запрос SQ (${reviewCount})`}
        </Button>
      )}
      {extra}
      {tabReviewCount !== null && (
        <FilterLabel>
          <input
            type="checkbox"
            checked={onlyReview}
            onChange={(e) => onOnlyReviewChange(e.target.checked)}
          />
          Только на проверку ({tabReviewCount})
        </FilterLabel>
      )}
      {hideRepeats !== null && (
        <FilterLabel title="Строки вне суммы, чьё число уже учтено в другой строке ревизии (повтор спецификации на чертеже, строка ВОР, повторяющая СП01, повтор ИИ). Признак — из того же отбора, что и ведомость материалов.">
          <input
            type="checkbox"
            checked={hideRepeats}
            onChange={(e) => onHideRepeatsChange(e.target.checked)}
          />
          Скрыть повторы вне суммы ({repeatCount})
        </FilterLabel>
      )}
    </Controls>
  );
}
